import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  Utensils,
  Activity,
  Flame,
  Dumbbell,
  Moon,
  Leaf,
  Heart,
  Check,
} from "lucide-react";
import "./LandingPage.css";
import {
  brand,
  coachImage,
  hero,
  proofStats,
  testimonials,
  steps,
  finalCta,
  menopause,
  fullStack,
} from "../mock";

const PILLAR_ICONS = { Utensils, Activity, Flame, Dumbbell, Moon, Leaf, Heart };

const LandingPage = () => {
  const navigate = useNavigate();

  const goToQuiz = (e) => {
    if (e) e.preventDefault();
    navigate("/quiz");
  };

  const scrollToQuiz = goToQuiz;
  const startQuiz = goToQuiz;

  return (
    <div className="sffx">
      {/* ============ TOP BAR ============ */}
      <div className="wrap">
        <div className="topbar">
          <div className="logo">{brand.name}</div>
          <nav className="topnav">
            <a
              className="mini-cta"
              href="#quiz"
              onClick={scrollToQuiz}
              data-testid="nav-take-quiz"
            >
              Take the Quiz
            </a>
            <a
              className="mini-link"
              href="https://sassyfitness.com"
              data-testid="nav-home-link"
            >
              Home
            </a>
          </nav>
        </div>
      </div>

      {/* ============ HERO ============ */}
      <div className="wrap">
        <section className="hero">
          <div>
            <span className="eyebrow">{hero.eyebrow}</span>
            <h1>
              {hero.titlePlain}
              <span className="gold">{hero.titleGold}</span>
            </h1>
            <p className="sub">
              Answer 4 quick questions and get your{" "}
              <strong>free custom 6-week fitness plan, delivered immediately</strong>{" "}
              — built to bring back the version of you that felt great, at the
              age you are now.
            </p>
            <a className="cta-btn" href="#quiz" onClick={scrollToQuiz}>
              {hero.cta}
            </a>
            <div className="cta-note">
              <span className="dot"></span> Free &nbsp;·&nbsp; Takes about a
              minute &nbsp;·&nbsp; Your plan is ready instantly
            </div>
          </div>
          <div className="hero-photo">
            <img src={coachImage} alt="Founder and coach" />
          </div>
        </section>
      </div>

      {/* ============ PROOF STRIP ============ */}
      <div className="wrap">
        <div className="proof-strip">
          {proofStats.map((s, i) => (
            <div className="stat" key={i}>
              <div className="num">{s.num}</div>
              <div className="lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ============ TRANSFORMATIONS ============ */}
      <div className="wrap wrap-cards">
        <section className="section">
          <h2 className="section-title">Real Women. Real Results.</h2>
          <p className="section-sub">
            These clients started exactly where you are — with the same quiz.
          </p>
          <div className="cards">
            {testimonials.map((t) => (
              <div className="card" key={t.id}>
                <div className="photo">
                  {t.image ? (
                    <img
                      src={t.image}
                      alt={t.who}
                      className={`client-img${t.rotate ? " rotate" : ""}${
                        t.contain ? " contain" : ""
                      }`}
                    />
                  ) : (
                    <div className="placeholder">
                      <Camera className="ph-icon" size={34} strokeWidth={1.5} />
                      <div className="ph-label">{t.label}</div>
                    </div>
                  )}
                </div>
                <div className="body">
                  <span className="result-chip">{t.chip}</span>
                  <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
                  <div className="who">
                    {(() => {
                      const idx = t.who.indexOf("·");
                      if (idx === -1) return t.who;
                      const nameAge = t.who.slice(0, idx).trim();
                      const descriptor = t.who.slice(idx + 1).trim();
                      return (
                        <>
                          <span className="who-name">{nameAge}</span>
                          <span className="who-desc">{descriptor}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ============ HOW IT WORKS ============ */}
      <div className="wrap">
        <section className="section" style={{ paddingTop: 0 }}>
          <h2 className="section-title">How It Works</h2>
          <p className="section-sub">
            From quiz to custom plan in about a minute.
          </p>
          <div className="steps">
            {steps.map((step) => (
              <div className="step" key={step.n}>
                <div className="n">{step.n}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ============ MENOPAUSE SECTION ============ */}
      <div className="wrap">
        <section className="menopause">
          <span className="eyebrow">{menopause.eyebrow}</span>
          <h2 className="meno-title">
            {menopause.title}
            <span className="gold">{menopause.titleGold}</span>
          </h2>
          <p className="meno-body">{menopause.body}</p>
          <blockquote className="meno-quote">
            {menopause.quote}
            <cite>{menopause.quoteAttr}</cite>
          </blockquote>
          <a className="cta-btn" href="#" onClick={startQuiz}>
            Take the 60-Second Quiz →
          </a>
        </section>
      </div>

      {/* ============ FULL STACK MODEL ============ */}
      <div className="wrap">
        <section className="fullstack">
          <span className="eyebrow">{fullStack.eyebrow}</span>
          <h2 className="fs-title">
            {fullStack.title}
            <span className="gold">{fullStack.titleGold}</span>
          </h2>
          <p className="fs-intro">{fullStack.intro}</p>
          <div className="fs-grid">
            {fullStack.pillars.map((p) => {
              const Icon = PILLAR_ICONS[p.icon];
              return (
                <div className="fs-pillar" key={p.name}>
                  <div className="fs-icon">{Icon && <Icon size={22} />}</div>
                  <div className="fs-pct">{p.pct}</div>
                  <div className="fs-name">{p.name}</div>
                </div>
              );
            })}
          </div>
          <p className="fs-split">{fullStack.splitLine}</p>
          <div className="fs-services">
            <div className="fs-services-label">All under one roof</div>
            <div className="fs-chips">
              {fullStack.services.map((s) => (
                <span className="fs-chip" key={s}>
                  <Check size={13} strokeWidth={3} /> {s}
                </span>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ============ FINAL CTA ============ */}
      <section className="final-cta" id="quiz">
        <h2>
          {finalCta.titlePlain}
          <span style={{ color: "var(--gold-bright)" }}>
            {finalCta.titleGold}
          </span>
        </h2>
        <p>{finalCta.sub}</p>
        <a className="cta-btn" href="#" onClick={startQuiz}>
          {finalCta.cta}
        </a>
        <div className="cta-note" style={{ justifyContent: "center" }}>
          <span className="dot"></span> Free &nbsp;·&nbsp; No credit card
          &nbsp;·&nbsp; Instant results
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer>
        <div className="wrap">
          © {brand.year} {brand.name} &nbsp;·&nbsp;{" "}
          <a
            href="https://sassyfitness.com/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
          >
            Privacy Policy
          </a>{" "}
          &nbsp;·&nbsp;{" "}
          <a
            href="https://sassyfitness.com/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
          >
            Terms
          </a>{" "}
          &nbsp;·&nbsp; This site is not part of Facebook/Meta.
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
